import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Shield, HeartPulse, Lock } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1582298538104-fe2e74c27f59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwbGF1Z2hpbmclMjB0b2dldGhlcnxlbnwwfHx8fDE3ODA5MjIyNjJ8MA&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background grain overflow-x-hidden" data-testid="landing-page">
      <nav className="flex items-center justify-between gap-3 px-4 sm:px-6 md:px-12 py-4 sm:py-5">
        <div className="flex shrink-0 items-center gap-2 font-display text-xl sm:text-2xl tracking-tight">
          <span className="inline-block w-7 h-7 shrink-0 bg-primary border-2 border-foreground rounded-md shadow-brutal-sm" />
          <span>HAEVEN</span>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <Link
            to="/auth"
            data-testid="landing-login-btn"
            className="brutal-btn whitespace-nowrap px-3 sm:px-4 py-2 text-sm sm:text-base bg-card"
          >
            Log in
          </Link>
          <Link
            to="/auth?mode=register"
            data-testid="landing-signup-btn"
            aria-label="Join HAEVEN"
            className="brutal-btn whitespace-nowrap px-3 sm:px-4 py-2 text-sm sm:text-base bg-primary text-primary-foreground"
          >
            <span className="sm:hidden">Join</span>
            <span className="hidden sm:inline">Join HAEVEN</span>
          </Link>
        </div>
      </nav>

      <section className="px-6 md:px-12 pt-10 pb-16 grid md:grid-cols-2 gap-10 items-center max-w-7xl mx-auto">
        <div className="space-y-6 animate-fade-up">
          <span className="inline-flex items-center gap-2 brutal-card px-3 py-1 text-sm bg-accent">
            <Sparkles size={16} strokeWidth={2.5} /> A safer way to social
          </span>
          <h1 className="text-5xl md:text-7xl leading-[0.95]">
            Social that <br />
            <span className="bg-accent border-2 border-foreground px-3 inline-block -rotate-1">feels good</span>
          </h1>
          <p className="text-lg max-w-lg text-muted-foreground">
            Share, connect, and message with privacy controls, optional safety prompts, mood check-ins, and no public like counts.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/auth?mode=register" data-testid="hero-cta-btn" className="brutal-btn px-6 py-3 bg-primary text-primary-foreground text-lg">Create your account</Link>
            <Link to="/auth" className="brutal-btn px-6 py-3 bg-card text-lg">I already have one</Link>
          </div>
        </div>
        <div className="relative">
          <div className="brutal-card overflow-hidden rotate-1">
            <img src={HERO_IMG} alt="Friends laughing" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 brutal-card bg-sage px-4 py-3 -rotate-3 max-w-[220px]">
            <p className="text-sm font-semibold">Pause before posting. Review the wording, then share when it feels right.</p>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl mb-8">What makes HAEVEN different</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { Icon: Shield, title: "Safety Prompts", body: "Optional checks can flag potentially harmful wording before you post or send.", bg: "bg-card" },
            { Icon: HeartPulse, title: "Wellness Built-in", body: "Mood check-ins, mindful breaks, and private reflection tools are available when you want them.", bg: "bg-sage" },
            { Icon: Lock, title: "Privacy Controls", body: "Choose who can see your activity with account, audience, and relationship controls.", bg: "bg-accent" },
          ].map(({ Icon, title, body, bg }, i) => (
            <div key={title} className={`brutal-card p-6 ${bg}`} data-testid={`feature-card-${i}`}>
              <Icon size={32} strokeWidth={2.5} />
              <h3 className="text-2xl mt-3">{title}</h3>
              <p className="mt-2 text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t-2 border-foreground py-6 px-6 md:px-12 text-sm flex flex-wrap justify-between gap-3">
        <span>© 2026 HAEVEN. Built with care.</span>
        <div className="flex gap-4">
          <Link to="/legal/privacy" data-testid="footer-privacy" className="underline">Privacy</Link>
          <Link to="/legal/terms" data-testid="footer-terms" className="underline">Terms</Link>
        </div>
        <span>a safer way to social</span>
      </footer>
    </div>
  );
}
